/**
 * useCurrentEvent.js
 *
 * Organizer EMS 全域活動 context，統一管理 topbar 與活動功能 route 的活動選擇。
 *
 * @author: wyan@yoursaas.com
 * @date: 2026-07-20
 */

import { computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { apiEventSettingsList } from '@/apis/backend';
import { changeDb } from '@/store/db';

let eventListRequest = null;
let eventListRequestOgzuid = '';
let eventListRequestVersion = 0;
let eventListVersion = 0;

const normalizeEvid = value => `${value || ''}`.trim();

export const useCurrentEvent = () => {
	const store = useStore();
	const route = useRoute();
	const router = useRouter();

	const events = computed(() => store.state.db.eventbasics || []);
	const currentEvid = computed(() => normalizeEvid(store.state.db.evid));
	const currentEvent = computed(() => {
		const stored = store.state.db.eventbasic || {};
		if (normalizeEvid(stored.evid) === currentEvid.value) return stored;
		return (
			events.value.find(
				item => normalizeEvid(item.evid) === currentEvid.value
			) || {}
		);
	});

	const routeAcceptsEvid = computed(() =>
		route.matched.some(record => record.path.includes(':evid'))
	);
	const routeShouldSyncEvid = computed(() => routeAcceptsEvid.value);

	const resetEventLocalState = () => {
		changeDb(store, 'evsections', []);
		changeDb(store, 'new_eventbasic', {});
		changeDb(store, 'new_evsections', []);
	};

	const selectEvent = async (evid, options = {}) => {
		const normalized = normalizeEvid(evid);
		const selected =
			events.value.find(item => normalizeEvid(item.evid) === normalized) ||
			{};

		if (!normalized || !selected.evid) {
			changeDb(store, 'evid', '');
			changeDb(store, 'eventbasic', {});
			resetEventLocalState();
			return false;
		}

		const changed = normalized !== currentEvid.value;
		changeDb(store, 'evid', normalized);
		changeDb(store, 'eventbasic', { ...selected });
		if (changed) resetEventLocalState();

		if (
			options.syncRoute !== false &&
			routeShouldSyncEvid.value &&
			normalizeEvid(route.params.evid) !== normalized
		) {
			await router.replace({
				name: route.name,
				params: { ...route.params, evid: normalized },
				query: route.query,
				hash: route.hash,
			});
		}

		return true;
	};

	const chooseInitialEvent = async () => {
		const routeEvid = normalizeEvid(route.params.evid);
		const storedEvid = currentEvid.value;
		const candidates = [routeEvid, storedEvid, events.value[0]?.evid];
		const matched = candidates.find(candidate =>
			events.value.some(
				item => normalizeEvid(item.evid) === normalizeEvid(candidate)
			)
		);

		return selectEvent(matched, {
			syncRoute: Boolean(routeShouldSyncEvid.value),
		});
	};

	const loadEvents = async (options = {}) => {
		const authOgzuid = store.state.settings.auth.accessOUID || '';
		const ogzuid =
			authOgzuid ||
			store.state.db.eventbasic?.ogzuid ||
			'';
		if (!ogzuid) return [];

		if (!options.force && events.value.length > 0 &&
			events.value.every(item => item.ogzuid === ogzuid)) {
			await chooseInitialEvent();
			return events.value;
		}

		// force 必須重新取得建立後的清單；一般載入只共用同組織的 request。
		if (options.force || !eventListRequest || eventListRequestOgzuid !== ogzuid) {
			eventListRequestOgzuid = ogzuid;
			eventListRequestVersion = ++eventListVersion;
			const request = apiEventSettingsList(ogzuid, {
				keyword: '',
				status: '',
				archive: '0',
				start: '',
				end: '',
				sort: 'evstime',
				sortorder: 'ASC',
			}).finally(() => {
				if (eventListRequest === request) {
					eventListRequest = null;
					eventListRequestOgzuid = '';
				}
			});
			eventListRequest = request;
		}

		const requestVersion = eventListRequestVersion;
		const response = await eventListRequest;
		if ((store.state.settings.auth.accessOUID || '') !== authOgzuid) {
			throw new Error('EVENT_ORGANIZATION_CHANGED');
		}
		// 更舊的回應不能覆蓋 force 或另一組織剛取得的活動 context。
		if (requestVersion !== eventListVersion) throw new Error('EVENT_LIST_SUPERSEDED');
		if (response?.data?.status !== 'OK') {
			throw new Error(
				response?.data?.message || response?.data?.code || 'EVENT_LIST_FAILED'
			);
		}

		if (!Array.isArray(response.data.dbo)) throw new Error('EVENT_LIST_INVALID');
		const rows = response.data.dbo;
		changeDb(store, 'eventbasics', rows);
		await chooseInitialEvent();
		return rows;
	};

	watch(
		() => route.params.evid,
		async evid => {
			const normalized = normalizeEvid(evid);
			if (!normalized || normalized === currentEvid.value) return;
			await selectEvent(normalized, { syncRoute: false });
		}
	);

	onMounted(() => {
		loadEvents().catch(error => {
			console.error('use-current-event, load-events, error=', error);
		});
	});

	return {
		events,
		currentEvid,
		currentEvent,
		loadEvents,
		selectEvent,
	};
};
